<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Auth from 'dashboard/api/auth';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import Avatar from 'next/avatar/Avatar.vue';
import SidebarProfileMenuStatus from '../sidebar/SidebarProfileMenuStatus.vue';
import CustomBrandPolicyWrapper from '../../components/CustomBrandPolicyWrapper.vue';
import {
  DropdownContainer,
  DropdownBody,
  DropdownSeparator,
  DropdownItem,
} from 'next/dropdown-menu/base';

const emit = defineEmits(['openKeyShortcutModal']);

const { t } = useI18n();

const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const accountId = useMapGetter('getCurrentAccountId');
const globalConfig = useMapGetter('globalConfig/get');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const showChatSupport = computed(
  () =>
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.CONTACT_CHATWOOT_SUPPORT_TEAM
    ) && globalConfig.value.chatwootInboxToken
);

const menuItems = computed(() => [
  {
    show: showChatSupport.value,
    showOnCustomBrandedInstance: false,
    label: t('SIDEBAR_ITEMS.CONTACT_SUPPORT'),
    icon: 'i-lucide-life-buoy',
    click: () => window.$chatwoot.toggle(),
  },
  {
    show: true,
    showOnCustomBrandedInstance: true,
    label: t('SIDEBAR_ITEMS.KEYBOARD_SHORTCUTS'),
    icon: 'i-lucide-keyboard',
    click: () => emit('openKeyShortcutModal'),
  },
  {
    show: true,
    showOnCustomBrandedInstance: true,
    label: t('SIDEBAR_ITEMS.PROFILE_SETTINGS'),
    icon: 'i-lucide-user-pen',
    link: { name: 'profile_settings_index' },
  },
  {
    show: true,
    showOnCustomBrandedInstance: true,
    label: t('SIDEBAR_ITEMS.APPEARANCE'),
    icon: 'i-lucide-palette',
    click: () => {
      const ninja = document.querySelector('ninja-keys');
      ninja.open({ parent: 'appearance_settings' });
    },
  },
  {
    show: true,
    showOnCustomBrandedInstance: false,
    label: t('SIDEBAR_ITEMS.DOCS'),
    icon: 'i-lucide-book',
    link: 'https://www.chatwoot.com/hc/user-guide/en',
    nativeLink: true,
    target: '_blank',
  },
  {
    show: true,
    showOnCustomBrandedInstance: false,
    label: t('SIDEBAR_ITEMS.CHANGELOG'),
    icon: 'i-lucide-scroll-text',
    link: 'https://www.chatwoot.com/changelog/',
    nativeLink: true,
    target: '_blank',
  },
  {
    show: currentUser.value.type === 'SuperAdmin',
    showOnCustomBrandedInstance: true,
    label: t('SIDEBAR_ITEMS.SUPER_ADMIN_CONSOLE'),
    icon: 'i-lucide-castle',
    link: '/super_admin',
    nativeLink: true,
    target: '_blank',
  },
  {
    show: true,
    showOnCustomBrandedInstance: true,
    label: t('SIDEBAR_ITEMS.LOGOUT'),
    icon: 'i-lucide-power',
    click: Auth.logout,
  },
]);

const allowedMenuItems = computed(() =>
  menuItems.value.filter(item => item.show)
);
</script>

<template>
  <DropdownContainer class="relative">
    <template #trigger="{ toggle, isOpen }">
      <button
        class="flex items-center justify-center rounded-lg hover:bg-n-alpha-1 transition-colors size-8"
        :class="{ 'bg-n-alpha-1': isOpen }"
        :title="currentUser.available_name"
        @click="toggle"
      >
        <Avatar
          :size="28"
          :name="currentUser.available_name"
          :src="currentUser.avatar_url"
          :status="currentUserAvailability"
          class="flex-shrink-0"
          rounded-full
        />
      </button>
    </template>

    <DropdownBody class="z-50 w-80 ltr:right-0 rtl:left-0">
      <SidebarProfileMenuStatus />
      <DropdownSeparator />
      <template v-for="item in allowedMenuItems" :key="item.label">
        <CustomBrandPolicyWrapper
          :show-on-custom-branded-instance="item.showOnCustomBrandedInstance"
        >
          <DropdownItem v-if="item.show" v-bind="item" />
        </CustomBrandPolicyWrapper>
      </template>
    </DropdownBody>
  </DropdownContainer>
</template>
